/**
 * Terms of Service Page
 *
 * Public legal page displaying NinjaCash terms of service.
 * Accessible without authentication for compliance (GDPR, CCPA, Play Store).
 * Matches Tokyo Night aesthetic with glass morphism.
 */

import { Link } from 'react-router-dom';
import './LegalPage.css';

export default function TermsPage() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        {/* Header */}
        <header className="legal-header">
          <Link to="/" className="legal-back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to App
          </Link>
          <div className="legal-brand">
            <span className="legal-logo">$</span>
            <span className="legal-app-name">NinjaCash</span>
          </div>
        </header>

        {/* Content */}
        <article className="legal-content">
          <h1 className="legal-title">Terms of Service</h1>
          <p className="legal-meta">Last Updated: January 19, 2026 | Version 1.1</p>

          {/* Overview */}
          <section className="legal-section">
            <h2>Overview</h2>
            <p>
              Welcome to <strong>NinjaCash</strong>, a personal finance tracking application. These Terms of Service govern your access to and use of NinjaCash, including our website, progressive web application (PWA), and related services.
            </p>
            <div className="legal-highlight warning">
              <p><strong>By using NinjaCash, you agree to these Terms. If you do not agree, do not use the Service.</strong></p>
            </div>
          </section>

          {/* Section 1 */}
          <section className="legal-section">
            <h2>1. Service Description</h2>
            
            <h3>1.1 What NinjaCash Is</h3>
            <p>NinjaCash is a <strong>personal finance tracking tool</strong> designed to help you:</p>
            <ul>
              <li><strong>Budget Tracking</strong> - Record income, expenses, and monitor spending patterns</li>
              <li><strong>Offline-First</strong> - Works without internet connection; data stored locally</li>
              <li><strong>Multi-Device Sync</strong> - Optional cloud synchronization across your devices</li>
              <li><strong>Manual Entry</strong> - You manually enter and categorize all financial data</li>
            </ul>

            <h3>1.2 What NinjaCash is NOT</h3>
            <div className="legal-highlight warning">
              <p><strong>NinjaCash is NOT a financial service.</strong> We do not:</p>
              <ul>
                <li>Connect to your bank accounts or financial institutions</li>
                <li>Transmit, hold, or process money</li>
                <li>Store credit card numbers or payment credentials</li>
                <li>Provide financial, investment, tax, or legal advice</li>
                <li>Act as a bank, broker, or financial institution</li>
                <li>Offer FDIC insurance or any deposit protection</li>
              </ul>
              <p><strong>We are a budgeting tool only.</strong></p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="legal-section">
            <h2>2. Eligibility</h2>
            <p>To use NinjaCash, you must:</p>
            <ul>
              <li>Be at least <strong>13 years old</strong> (or the minimum age required in your jurisdiction)</li>
              <li>Have the legal capacity to enter into binding agreements</li>
              <li>Provide accurate registration information</li>
              <li>Comply with all applicable laws in your jurisdiction</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="legal-section">
            <h2>3. Account Registration & Security</h2>
            
            <h3>3.1 Account Creation</h3>
            <ul>
              <li>You create an account using <strong>Google OAuth</strong></li>
              <li>You must provide accurate and current information</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials</li>
              <li>One account per person - do not share accounts</li>
            </ul>

            <h3>3.2 Account Security</h3>
            <p><strong>You are responsible for:</strong></p>
            <ul>
              <li>Safeguarding your Google account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Immediately notifying us of any security breach or unauthorized use</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="legal-section">
            <h2>4. User Responsibilities</h2>
            
            <h3>4.1 Data Accuracy</h3>
            <ul>
              <li><strong>You are solely responsible</strong> for the accuracy, completeness, and legality of all financial data you enter</li>
              <li>We do not verify, validate, or audit your data</li>
              <li>Incorrect data entry may lead to inaccurate budgets and financial tracking</li>
            </ul>

            <h3>4.2 Prohibited Activities</h3>
            <p>You may NOT:</p>
            <ul>
              <li>Use the Service for money laundering, fraud, or illegal activities</li>
              <li>Attempt to reverse engineer, decompile, or hack the Service</li>
              <li>Upload malicious code, viruses, or harmful software</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Violate any third-party rights</li>
              <li>Impersonate another person or entity</li>
            </ul>
            <p><strong>Violation of these terms may result in immediate account termination.</strong></p>
          </section>

          {/* Section 5 - Financial Disclaimer */}
          <section className="legal-section">
            <h2>5. Financial Disclaimer</h2>
            <div className="legal-highlight warning">
              <h3>IMPORTANT: No Financial Advice</h3>
              <p><strong>NinjaCash is a budgeting tool, NOT a financial advisor.</strong></p>
              <div className="legal-table-wrapper">
                <table className="legal-table">
                  <thead>
                    <tr>
                      <th>What We Are</th>
                      <th>What We Are NOT</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Budget tracking software</td>
                      <td>Financial advisors</td>
                    </tr>
                    <tr>
                      <td>Data organization tool</td>
                      <td>Tax professionals</td>
                    </tr>
                    <tr>
                      <td>Personal finance tracker</td>
                      <td>Investment advisors</td>
                    </tr>
                    <tr>
                      <td>Expense categorization</td>
                      <td>Legal counsel</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <p><strong>DISCLAIMER:</strong></p>
            <p>The information provided by NinjaCash is for <strong>informational and educational purposes only</strong>. This app does NOT constitute financial, investment, tax, or legal advice.</p>
            <ul>
              <li><strong>No Advice:</strong> Nothing in NinjaCash constitutes professional financial advice</li>
              <li><strong>Consult Professionals:</strong> Always consult qualified professionals before making financial decisions</li>
              <li><strong>No Guarantees:</strong> We make no guarantees about financial outcomes from using our Service</li>
              <li><strong>Your Responsibility:</strong> You are solely responsible for your financial decisions</li>
              <li><strong>No Liability:</strong> We are NOT liable for any financial losses resulting from your use of NinjaCash</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="legal-section">
            <h2>6. Pricing & Payment Terms</h2>
            
            <h3>6.1 Free Tier</h3>
            <ul>
              <li><strong>Local storage</strong> is FREE forever</li>
              <li>No credit card required for basic offline use</li>
              <li>Full functionality for single-device use</li>
            </ul>

            <h3>6.2 Paid Sync (Optional)</h3>
            <p>If you choose to purchase cloud synchronization:</p>
            <ul>
              <li><strong>Pricing:</strong> Displayed at time of purchase; subject to change with notice</li>
              <li><strong>Billing:</strong> Recurring subscription (monthly/annual)</li>
              <li><strong>Cancellation:</strong> Cancel anytime; access continues until end of billing period</li>
              <li><strong>Refunds:</strong> No refunds except as required by law</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="legal-section">
            <h2>7. Intellectual Property</h2>
            
            <h3>7.1 Your Data</h3>
            <p><strong>You own your financial data.</strong> We claim no ownership rights to the information you enter into NinjaCash.</p>

            <h3>7.2 NinjaCash Code & Branding</h3>
            <ul>
              <li><strong>Proprietary Software:</strong> NinjaCash is proprietary software. All rights reserved.</li>
              <li><strong>Trademarks:</strong> "NinjaCash" name, logos, and branding remain our property</li>
              <li><strong>You may NOT:</strong> Copy, modify, distribute, or reverse engineer the software without permission</li>
              <li><strong>You may NOT:</strong> Use our name or branding to imply endorsement without permission</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="legal-section">
            <h2>8. Data Storage & Synchronization</h2>
            
            <h3>8.1 Local Storage (Free Tier)</h3>
            <ul>
              <li>Data stored on <strong>your device</strong> using browser local storage (IndexedDB)</li>
              <li>You are responsible for backing up local data</li>
              <li>Clearing browser data will delete local records</li>
            </ul>

            <h3>8.2 Cloud Sync (Optional)</h3>
            <ul>
              <li>Data encrypted and stored on our servers</li>
              <li>Subject to our <strong>Privacy Policy</strong></li>
              <li>We do NOT access or read your financial data</li>
              <li>Sync may be delayed or unavailable due to technical issues</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="legal-section">
            <h2>9. No Warranty Disclaimer</h2>
            <div className="legal-highlight">
              <p><strong>NinjaCash is provided "AS IS" and "AS AVAILABLE" without warranties of any kind.</strong></p>
            </div>
            <p>We disclaim ALL warranties, express or implied, including but not limited to:</p>
            <ul>
              <li><strong>Merchantability</strong> - fitness for a particular purpose</li>
              <li><strong>Accuracy</strong> - accuracy, reliability, or completeness of data</li>
              <li><strong>Availability</strong> - uninterrupted or error-free service</li>
              <li><strong>Security</strong> - absolute security of data</li>
              <li><strong>Results</strong> - any particular financial outcome</li>
            </ul>
            <p><strong>We do NOT guarantee:</strong></p>
            <ul>
              <li>100% uptime or availability</li>
              <li>Data accuracy or calculation correctness</li>
              <li>Compatibility with all devices/browsers</li>
              <li>Protection against all security threats</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section className="legal-section">
            <h2>10. Limitation of Liability</h2>
            <div className="legal-highlight warning">
              <p><strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong></p>
              <p>Our total liability to you shall NOT exceed the <strong>greater of:</strong></p>
              <ol>
                <li>The amount you paid us in the past 12 months, OR</li>
                <li><strong>$100 USD</strong></li>
              </ol>
            </div>
            <p><strong>WE ARE NOT LIABLE FOR:</strong></p>
            <ul>
              <li>Indirect, consequential, incidental, or punitive damages</li>
              <li>Lost profits, lost data, or lost opportunities</li>
              <li>Damages resulting from third-party services</li>
            </ul>
          </section>

          {/* Section 11 - Indemnification */}
          <section className="legal-section">
            <h2>11. Indemnification</h2>
            <p><strong>You agree to indemnify, defend, and hold harmless</strong> NinjaCash, its developers, affiliates, and partners from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:</p>
            <ul>
              <li>Your use or misuse of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any laws or third-party rights</li>
              <li>Inaccuracy or illegality of data you enter</li>
              <li>Unauthorized access to your account due to your negligence</li>
            </ul>
          </section>

          {/* Section 12 */}
          <section className="legal-section">
            <h2>12. Account Deletion Rights</h2>
            <p><strong>You have the right to:</strong></p>
            <ul>
              <li><strong>Delete Account</strong> - Permanently delete your NinjaCash account anytime</li>
              <li><strong>Delete Data</strong> - Request deletion of cloud-stored financial data</li>
              <li><strong>Export Data</strong> - Download your data before deletion (JSON export)</li>
              <li><strong>No Explanation Required</strong> - Delete without providing a reason</li>
            </ul>
            <h3>How to Delete</h3>
            <ol>
              <li>Log into NinjaCash</li>
              <li>Go to <strong>Settings &gt; Account &gt; Delete Account</strong></li>
              <li>Confirm deletion</li>
              <li>Cloud data deleted within <strong>30 days</strong></li>
            </ol>
            <p><strong>Important:</strong> Deletion is permanent and irreversible.</p>
          </section>

          {/* Section 13 */}
          <section className="legal-section">
            <h2>13. Termination</h2>
            
            <h3>12.1 Termination by You</h3>
            <p>You may terminate your account at any time using the "Delete Account" feature in Settings.</p>

            <h3>12.2 Termination by Us</h3>
            <p>We may suspend or terminate your account immediately if you:</p>
            <ul>
              <li>Violate these Terms or our policies</li>
              <li>Engage in illegal or harmful activities</li>
              <li>Abuse or exploit the Service</li>
              <li>Remain inactive for extended periods (12+ months)</li>
            </ul>
          </section>

          {/* Section 14 */}
          <section className="legal-section">
            <h2>14. Privacy & Data Protection</h2>
            <p>Your use of NinjaCash is also governed by our <strong>Privacy Policy</strong>, which explains:</p>
            <ul>
              <li>What data we collect</li>
              <li>How we use and protect your data</li>
              <li>Your privacy rights (GDPR, CCPA, etc.)</li>
            </ul>
            <p><strong>Key Points:</strong></p>
            <ul>
              <li>We do NOT sell your financial data</li>
              <li>We do NOT access or read your transactions</li>
              <li>Data encrypted in transit and at rest</li>
              <li>You control data sharing and deletion</li>
            </ul>
            <p><Link to="/privacy">Read our Privacy Policy</Link></p>
          </section>

          {/* Section 15 */}
          <section className="legal-section">
            <h2>15. Modifications to Terms</h2>
            <p>We may update these Terms at any time. For material changes:</p>
            <ul>
              <li>We will notify you via email (if provided)</li>
              <li>We will post notice in the app</li>
              <li>We will update the "Last Updated" date</li>
              <li>Changes take effect <strong>30 days after notice</strong></li>
            </ul>
            <p><strong>Continued use = acceptance</strong> of new Terms.</p>
          </section>

          {/* Section 16 */}
          <section className="legal-section">
            <h2>16. Governing Law & Jurisdiction</h2>
            <p>These Terms are governed by the laws of the <strong>State of California, United States</strong>, without regard to conflict of law principles.</p>
            <p>You agree that any disputes will be resolved in the courts located in California.</p>
          </section>

          {/* Section 17 - Dispute Resolution */}
          <section className="legal-section">
            <h2>17. Dispute Resolution</h2>
            
            <h3>17.1 Informal Resolution</h3>
            <p><strong>Before filing any legal action</strong>, you agree to:</p>
            <ol>
              <li>Contact us via GitHub Issues with a detailed description of the dispute</li>
              <li>Attempt to resolve the issue informally for <strong>30 days</strong></li>
              <li>Provide us reasonable opportunity to address your concerns</li>
            </ol>

            <h3>17.2 Class Action Waiver</h3>
            <div className="legal-highlight warning">
              <p><strong>You agree to resolve disputes individually, NOT as part of a class, collective, or representative action</strong>, to the extent permitted by law.</p>
            </div>
            <p>This means you waive any right to:</p>
            <ul>
              <li>Participate in a class action lawsuit against NinjaCash</li>
              <li>Participate in class-wide arbitration</li>
              <li>Have disputes decided by a jury as part of a class action</li>
            </ul>

            <h3>17.3 Small Claims Exception</h3>
            <p>Either party may bring qualifying claims in small claims court (under applicable jurisdictional limits).</p>
          </section>

          {/* Section 18 - Third-Party Services */}
          <section className="legal-section">
            <h2>18. Third-Party Services</h2>
            <p>NinjaCash integrates with third-party services:</p>
            <div className="legal-table-wrapper">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Purpose</th>
                    <th>Terms</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Google OAuth</td>
                    <td>Authentication</td>
                    <td><a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Google Terms</a></td>
                  </tr>
                  <tr>
                    <td>Supabase</td>
                    <td>Cloud database & auth</td>
                    <td><a href="https://supabase.com/terms" target="_blank" rel="noopener noreferrer">Supabase Terms</a></td>
                  </tr>
                  <tr>
                    <td>Cloudflare</td>
                    <td>Frontend hosting</td>
                    <td><a href="https://www.cloudflare.com/terms/" target="_blank" rel="noopener noreferrer">Cloudflare Terms</a></td>
                  </tr>
                  <tr>
                    <td>Railway</td>
                    <td>API hosting</td>
                    <td><a href="https://railway.app/legal/terms" target="_blank" rel="noopener noreferrer">Railway Terms</a></td>
                  </tr>
                  <tr>
                    <td>Polar.sh</td>
                    <td>Payment processing</td>
                    <td><a href="https://polar.sh/legal/terms" target="_blank" rel="noopener noreferrer">Polar Terms</a></td>
                  </tr>
                  <tr>
                    <td>Sentry</td>
                    <td>Error monitoring</td>
                    <td><a href="https://sentry.io/terms/" target="_blank" rel="noopener noreferrer">Sentry Terms</a></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p><strong>You acknowledge:</strong></p>
            <ul>
              <li>We are NOT responsible for third-party services</li>
              <li>Third-party terms apply to their services</li>
              <li>Disruptions to third-party services may affect NinjaCash</li>
            </ul>
          </section>

          {/* Section 19 */}
          <section className="legal-section">
            <h2>19. Contact & Support</h2>
            <p>For questions, support, or legal inquiries, please use GitHub Issues:</p>
            <ul>
              <li><strong>GitHub:</strong> <a href="https://github.com/TakashiKyoto/ninjacash/issues" target="_blank" rel="noopener noreferrer">github.com/TakashiKyoto/ninjacash/issues</a></li>
            </ul>
            <p>GitHub Issues is our primary support channel. You can also find the link in the app's Settings page and on the login screen.</p>
          </section>

          {/* Acknowledgment */}
          <section className="legal-section">
            <h2>20. Acknowledgment</h2>
            <div className="legal-highlight">
              <p><strong>BY USING NINJACASH, YOU ACKNOWLEDGE THAT:</strong></p>
              <ul>
                <li>You have read and understood these Terms of Service</li>
                <li>You agree to be bound by these Terms</li>
                <li>You understand NinjaCash is NOT a financial service or advisor</li>
                <li>You are responsible for the accuracy of your financial data</li>
                <li>You accept the limitations of liability and warranty disclaimers</li>
              </ul>
              <p><strong>If you do NOT agree, you must immediately stop using NinjaCash.</strong></p>
            </div>
          </section>

          {/* Summary */}
          <section className="legal-section">
            <h2>Summary</h2>
            <div className="legal-summary">
              <div className="summary-item">
                <span className="summary-question">Is NinjaCash a bank?</span>
                <span className="summary-answer no">No - Budgeting tool only</span>
              </div>
              <div className="summary-item">
                <span className="summary-question">Do you provide financial advice?</span>
                <span className="summary-answer no">No - Consult professionals</span>
              </div>
              <div className="summary-item">
                <span className="summary-question">Who owns my data?</span>
                <span className="summary-answer yes">You - We claim no ownership</span>
              </div>
              <div className="summary-item">
                <span className="summary-question">Can I delete my account?</span>
                <span className="summary-answer yes">Yes - Anytime via Settings</span>
              </div>
            </div>
          </section>
        </article>

        {/* Footer */}
        <footer className="legal-footer">
          <p className="legal-footer-disclaimer">
            NinjaCash is a personal finance tracking tool. This app does NOT constitute financial, investment, tax, or legal advice. Always consult qualified professionals for financial guidance.
          </p>
          <p className="legal-footer-date">Last Updated: January 19, 2026</p>
          <div className="legal-footer-links">
            <Link to="/privacy">Privacy Policy</Link>
            <span className="legal-divider">|</span>
            <Link to="/">Back to NinjaCash</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
